/**
 * The documents of one vehicle: Fahrzeugausweis, purchase and leasing contracts, insurance policy.
 *
 * Built on the pattern of BabDocumentViewerModal: the bytes come through our API and are shown
 * from a blob: URL in an iframe. The SWA sends `X-Frame-Options: DENY` with its own responses and
 * the CSP has `frame-src 'self' blob:`, so a blob carries no header that could block it. Images go
 * through the same iframe, because `img-src` does not allow blob:.
 *
 * Documents belong to the VEHICLE (CompanyCode + PlateKey), so the same list opens from the row
 * of the old driver and from the row of the new one. The ValidUntil of a leasing contract is the
 * document's own date: it never changes LeasingExpiryDate on the car, the banner only says when
 * the two disagree.
 *
 * Every upload is also copied into SharePoint as an archive (migration 225). Each entry shows
 * "Open in SharePoint" once that worked, or "Copy failed – retry"; an entry that was never copied
 * (uploaded before the copy existed) offers "Copy to SharePoint" while the copy is switched on.
 * Edits and removals here are not mirrored there.
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
    X, Loader2, AlertTriangle, Download, FileText, Image as ImageIcon, Upload, Pencil, Trash2,
    ExternalLink, RefreshCw,
} from 'lucide-react';
import { companyCarsApi } from '../../services/companyCarsApi';
import { useTopmostEscape } from '../../hooks/useTopmostEscape';
import { useToast } from '../ui/Toast';
import { ConfirmModal } from '../ui/ConfirmModal';
import {
    CAR_DOCUMENT_TYPES,
    type CompanyCar, type CarDocument, type CarDocumentMetadata, type CarDocumentType,
} from '../../types/companyCars';

interface Props {
    car: CompanyCar;
    canEdit: boolean;
    onClose: () => void;
    /** Count or expiry changed: the fleet list should reload. */
    onChanged: () => void;
}

const typeLabel = (t: CarDocumentType) => CAR_DOCUMENT_TYPES.find(x => x.value === t)?.label ?? t;

/** 'YYYY-MM-DD' -> 'DD.MM.YYYY'. String in, string out: no Date, no timezone to lose a day to. */
function formatDay(value: string | null): string {
    return value && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10).split('-').reverse().join('.') : '';
}

function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function todayIso(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
        reader.onerror = () => reject(new Error('The file could not be read.'));
        reader.readAsDataURL(file);
    });
}

function ValidityBadge({ until }: { until: string | null }) {
    if (!until) return null;
    const day = until.slice(0, 10);
    const expired = day < todayIso();
    return (
        <span className={`rounded px-1.5 py-0.5 text-[11px] ${expired ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
            {expired ? 'Expired ' : 'until '}{formatDay(day)}
        </span>
    );
}

const EMPTY_META: CarDocumentMetadata = { documentType: 'registration', validFrom: null, validUntil: null, description: null };

/** The SharePoint state of one entry: the link, the retry, the first copy, or nothing. */
function SharePointStatus({ doc, canEdit, enabled, copying, onCopy }: {
    doc: CarDocument; canEdit: boolean; enabled: boolean; copying: boolean; onCopy: () => void;
}) {
    if (doc.spWebUrl) {
        return (
            <a href={doc.spWebUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                <ExternalLink className="h-3 w-3" /> Open in SharePoint
            </a>
        );
    }
    if (doc.spCopyError) {
        if (!canEdit) return <span className="text-red-600" title={doc.spCopyError}>SharePoint copy failed</span>;
        return (
            <button type="button" disabled={copying} title={doc.spCopyError}
                onClick={e => { e.stopPropagation(); onCopy(); }}
                className="inline-flex items-center gap-1 text-red-600 hover:underline disabled:opacity-50">
                {copying ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Copy failed – retry
            </button>
        );
    }
    if (!canEdit || !enabled) return null;
    return (
        <button type="button" disabled={copying}
            onClick={e => { e.stopPropagation(); onCopy(); }}
            className="inline-flex items-center gap-1 text-gray-500 hover:text-blue-600 hover:underline disabled:opacity-50">
            {copying ? <Loader2 className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3" />} Copy to SharePoint
        </button>
    );
}

export default function CarDocumentsModal({ car, canEdit, onClose, onChanged }: Props) {
    const { toast } = useToast();
    const [documents, setDocuments] = useState<CarDocument[]>([]);
    const [plateKey, setPlateKey] = useState<string | null>(car.PlateKey);
    const [listLoading, setListLoading] = useState(true);
    const [listError, setListError] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<number | null>(null);

    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [viewLoading, setViewLoading] = useState(false);
    const [viewError, setViewError] = useState<string | null>(null);

    // Upload form, or the metadata of one document while it is edited.
    const [meta, setMeta] = useState<CarDocumentMetadata>(EMPTY_META);
    const [file, setFile] = useState<File | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [busy, setBusy] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [deleting, setDeleting] = useState<CarDocument | null>(null);
    const [spEnabled, setSpEnabled] = useState(false);
    const [copyingId, setCopyingId] = useState<number | null>(null);
    const fileInput = useRef<HTMLInputElement>(null);

    useTopmostEscape(true, onClose);

    const load = useCallback(async (select?: number) => {
        setListLoading(true);
        setListError(null);
        try {
            const res = await companyCarsApi.listDocuments(car.Id);
            setDocuments(res.documents);
            setPlateKey(res.plateKey);
            setSpEnabled(res.sharePointCopyEnabled === true);
            setSelectedId(prev => {
                const want = select ?? prev;
                return res.documents.some(d => d.documentId === want) ? want! : res.documents[0]?.documentId ?? null;
            });
        } catch (err) {
            setListError(err instanceof Error ? err.message : 'The documents could not be loaded.');
        } finally {
            setListLoading(false);
        }
    }, [car.Id]);

    useEffect(() => { void load(); }, [load]);

    const selected = documents.find(d => d.documentId === selectedId) ?? null;

    useEffect(() => {
        if (!selected) { setBlobUrl(null); return; }
        let active = true;
        let created: string | null = null;
        setViewLoading(true);
        setViewError(null);
        setBlobUrl(null);
        companyCarsApi.fetchDocumentContent(selected.documentId)
            .then(blob => {
                if (!active) return;
                created = URL.createObjectURL(blob);
                setBlobUrl(created);
            })
            .catch(err => { if (active) setViewError(err instanceof Error ? err.message : 'The document could not be opened.'); })
            .finally(() => { if (active) setViewLoading(false); });
        // Revoked on every change AND on unmount; revoking right after assigning it to the iframe
        // would race the browser's PDF viewer and show an empty frame.
        return () => {
            active = false;
            if (created) URL.revokeObjectURL(created);
        };
    }, [selected?.documentId]); // eslint-disable-line react-hooks/exhaustive-deps

    const grouped = useMemo(() => CAR_DOCUMENT_TYPES
        .map(t => ({ ...t, docs: documents.filter(d => d.documentType === t.value) }))
        .filter(g => g.docs.length > 0), [documents]);

    // The leasing contract with the latest ValidUntil against the car's own field.
    const leasingHint = useMemo(() => {
        const until = documents
            .filter(d => d.documentType === 'leasing' && d.validUntil)
            .map(d => d.validUntil!.slice(0, 10))
            .sort()
            .pop();
        const carDate = car.LeasingExpiryDate?.slice(0, 10) ?? null;
        if (!until || until === carDate) return null;
        return carDate
            ? `The latest leasing contract runs until ${formatDay(until)}; the car record says ${formatDay(carDate)}. The fleet list and the export use the car record.`
            : `The latest leasing contract runs until ${formatDay(until)}; the car record has no leasing expiry date. The fleet list and the export use the car record.`;
    }, [documents, car.LeasingExpiryDate]);

    const resetForm = () => {
        setMeta(EMPTY_META);
        setFile(null);
        setEditingId(null);
        setFormError(null);
        if (fileInput.current) fileInput.current.value = '';
    };

    const startEdit = (doc: CarDocument) => {
        setEditingId(doc.documentId);
        setMeta({
            documentType: doc.documentType,
            validFrom: doc.validFrom,
            validUntil: doc.validUntil,
            description: doc.description,
        });
        setFile(null);
        setFormError(null);
    };

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError(null);
        const body: CarDocumentMetadata = {
            documentType: meta.documentType,
            validFrom: meta.validFrom || null,
            validUntil: meta.validUntil || null,
            description: meta.description?.trim() || null,
        };
        setBusy(true);
        try {
            if (editingId !== null) {
                await companyCarsApi.updateDocument(editingId, body);
                toast('Document updated', 'success');
                const id = editingId;
                resetForm();
                await load(id);
            } else {
                if (!file) { setFormError('Choose a file first.'); return; }
                const saved = await companyCarsApi.uploadDocument(car.Id, {
                    ...body,
                    fileName: file.name,
                    // Mobile Safari sends an empty type; the server then decides by the extension.
                    contentType: file.type,
                    fileBase64: await fileToBase64(file),
                });
                if (saved.duplicate) toast('This file is already on this vehicle', 'info');
                // The upload has succeeded either way; a failed archive copy can be retried from the list.
                else if (saved.spCopyError) toast('Document added. The SharePoint copy failed – retry it from the list.', 'error');
                else toast('Document added', 'success');
                resetForm();
                await load(saved.documentId);
            }
            onChanged();
        } catch (err) {
            // The server's sentence verbatim: it names the field and, for HEIC, the fix on the phone.
            setFormError(err instanceof Error ? err.message : 'The document could not be saved.');
        } finally {
            setBusy(false);
        }
    };

    const copyToSharePoint = async (doc: CarDocument) => {
        setCopyingId(doc.documentId);
        try {
            const result = await companyCarsApi.copyDocumentToSharePoint(doc.documentId);
            if (result.spCopyError) toast(`The SharePoint copy failed: ${result.spCopyError}`, 'error');
            else toast(result.alreadyCopied ? 'Already in SharePoint' : 'Copied to SharePoint', 'success');
            await load(doc.documentId);
        } catch (err) {
            toast(err instanceof Error ? err.message : 'The SharePoint copy failed.', 'error');
        } finally {
            setCopyingId(null);
        }
    };

    const confirmDelete = async () => {
        if (!deleting) return;
        try {
            await companyCarsApi.deleteDocument(deleting.documentId);
            toast('Document removed', 'success');
            if (editingId === deleting.documentId) resetForm();
            await load();
            onChanged();
        } catch (err) {
            toast(err instanceof Error ? err.message : 'The document could not be removed.', 'error');
        } finally {
            setDeleting(null);
        }
    };

    const inputCls = 'w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500';
    const labelCls = 'block text-xs font-medium text-gray-600 mb-0.5';
    const title = [car.BrandName, car.CarModel].filter(Boolean).join(' ') || 'Car';

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
            <div
                role="dialog"
                aria-modal="true"
                aria-label={`Documents of ${title} ${car.LicensePlate || ''}`}
                className="bg-white rounded-lg shadow-xl w-[96vw] max-w-[1200px] h-[90vh] flex flex-col"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200">
                    <div className="min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <FileText className="h-5 w-5 text-gray-400 shrink-0" />
                            <span className="truncate">Documents · {title}</span>
                        </h3>
                        <p className="mt-0.5 text-sm text-gray-500 truncate">
                            {[car.LicensePlate, car.DriverName, car.CompanyCode].filter(Boolean).join(' · ')}
                        </p>
                    </div>
                    <button onClick={onClose} className="ml-4 shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" title="Close" aria-label="Close">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {leasingHint && (
                    <div className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-6 py-2 text-xs text-amber-800">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        <p>{leasingHint}</p>
                    </div>
                )}

                <div className="flex flex-1 min-h-0 flex-col md:flex-row">
                    {/* Left: upload / edit form and the list */}
                    <div className="md:w-80 shrink-0 border-b md:border-b-0 md:border-r border-gray-200 flex flex-col min-h-0 max-h-[45vh] md:max-h-none">
                        {canEdit && plateKey && (
                            <form onSubmit={submit} className="p-4 space-y-2 border-b border-gray-200">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {editingId !== null ? 'Edit document' : 'Add document'}
                                    </span>
                                    {editingId !== null && (
                                        <button type="button" onClick={resetForm} className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                                    )}
                                </div>
                                <div>
                                    <label className={labelCls} htmlFor="doc-type">Type</label>
                                    <select id="doc-type" value={meta.documentType}
                                        onChange={e => setMeta(m => ({ ...m, documentType: e.target.value as CarDocumentType }))}
                                        className={inputCls}>
                                        {CAR_DOCUMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className={labelCls} htmlFor="doc-from">Valid from</label>
                                        <input id="doc-from" type="date" value={meta.validFrom?.slice(0, 10) || ''}
                                            onChange={e => setMeta(m => ({ ...m, validFrom: e.target.value || null }))} className={inputCls} />
                                    </div>
                                    <div>
                                        <label className={labelCls} htmlFor="doc-until">Valid until</label>
                                        <input id="doc-until" type="date" value={meta.validUntil?.slice(0, 10) || ''}
                                            onChange={e => setMeta(m => ({ ...m, validUntil: e.target.value || null }))} className={inputCls} />
                                    </div>
                                </div>
                                <div>
                                    <label className={labelCls} htmlFor="doc-desc">Description (optional)</label>
                                    <input id="doc-desc" type="text" maxLength={200} value={meta.description || ''}
                                        onChange={e => setMeta(m => ({ ...m, description: e.target.value }))} className={inputCls} />
                                </div>
                                {editingId === null && (
                                    <div>
                                        <label className={labelCls} htmlFor="doc-file">File (PDF, JPG or PNG, max. 10 MB)</label>
                                        <input id="doc-file" ref={fileInput} type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                                            onChange={e => setFile(e.target.files?.[0] ?? null)}
                                            className="block w-full text-xs text-gray-600 file:mr-2 file:rounded file:border-0 file:bg-gray-100 file:px-2 file:py-1 file:text-xs file:text-gray-700 hover:file:bg-gray-200" />
                                    </div>
                                )}
                                {formError && <p className="text-xs text-red-600">{formError}</p>}
                                <button type="submit" disabled={busy || (editingId === null && !file)}
                                    className="flex w-full items-center justify-center gap-1.5 rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-40">
                                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : editingId !== null ? <Pencil className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
                                    {editingId !== null ? 'Save changes' : 'Upload'}
                                </button>
                            </form>
                        )}

                        <div className="flex-1 overflow-y-auto p-2">
                            {listLoading && (
                                <div className="flex items-center justify-center py-8 text-sm text-gray-500">
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin text-blue-500" /> Loading…
                                </div>
                            )}
                            {!listLoading && listError && <p className="px-2 py-4 text-sm text-red-600">{listError}</p>}
                            {!listLoading && !listError && !plateKey && (
                                <p className="px-2 py-4 text-sm text-gray-500">
                                    This car has no usable license plate. Enter the plate before adding documents.
                                </p>
                            )}
                            {!listLoading && !listError && plateKey && documents.length === 0 && (
                                <p className="px-2 py-4 text-sm text-gray-500">No documents on this vehicle yet.</p>
                            )}
                            {grouped.map(group => (
                                <div key={group.value} className="mb-3">
                                    <div className="px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">{group.label}</div>
                                    {group.docs.map(doc => {
                                        const isSelected = doc.documentId === selectedId;
                                        const Icon = doc.contentType === 'application/pdf' ? FileText : ImageIcon;
                                        return (
                                            <div key={doc.documentId}
                                                className={`group flex items-start gap-2 rounded px-2 py-1.5 cursor-pointer ${isSelected ? 'bg-blue-50 ring-1 ring-blue-200' : 'hover:bg-gray-50'}`}
                                                onClick={() => setSelectedId(doc.documentId)}>
                                                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                                                <div className="min-w-0 flex-1">
                                                    <div className="truncate text-sm text-gray-800" title={doc.fileName}>{doc.description || doc.fileName}</div>
                                                    <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-gray-500">
                                                        <ValidityBadge until={doc.validUntil} />
                                                        {doc.validFrom && <span>from {formatDay(doc.validFrom)}</span>}
                                                        <span>{formatBytes(doc.sizeBytes)}</span>
                                                    </div>
                                                    <div className="mt-0.5 truncate text-[11px] text-gray-400" title={doc.uploadedBy}>
                                                        {doc.uploadedBy.split('@')[0]} · {formatDay(doc.uploadedAt)}
                                                        {/* Uploaded on another row of the same vehicle, older or newer (driver change). */}
                                                        {doc.uploadedOnCarId !== car.Id && ' · other row of this car'}
                                                    </div>
                                                    <div className="mt-0.5 text-[11px]">
                                                        <SharePointStatus doc={doc} canEdit={canEdit} enabled={spEnabled}
                                                            copying={copyingId === doc.documentId}
                                                            onCopy={() => void copyToSharePoint(doc)} />
                                                    </div>
                                                </div>
                                                {canEdit && (
                                                    <div className="flex shrink-0 items-center gap-0.5">
                                                        <button type="button" title="Edit type and dates" aria-label="Edit"
                                                            onClick={e => { e.stopPropagation(); startEdit(doc); }}
                                                            className="p-1 text-gray-400 hover:text-blue-600"><Pencil className="h-3.5 w-3.5" /></button>
                                                        <button type="button" title="Remove" aria-label="Remove"
                                                            onClick={e => { e.stopPropagation(); setDeleting(doc); }}
                                                            className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right: the viewer */}
                    <div className="flex flex-1 min-h-0 flex-col">
                        <div className="flex-1 overflow-hidden bg-gray-100 p-3">
                            {!selected && !listLoading && (
                                <div className="flex h-full items-center justify-center text-sm text-gray-400">
                                    {documents.length ? 'Select a document.' : 'Nothing to show yet.'}
                                </div>
                            )}
                            {selected && viewLoading && (
                                <div className="flex h-full items-center justify-center">
                                    <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                                    <span className="ml-2 text-gray-500">Loading document…</span>
                                </div>
                            )}
                            {selected && !viewLoading && viewError && (
                                <div className="flex h-full flex-col items-center justify-center px-8 text-center">
                                    <AlertTriangle className="h-8 w-8 text-amber-500" />
                                    <p className="mt-3 max-w-lg text-sm text-gray-700">{viewError}</p>
                                </div>
                            )}
                            {selected && !viewLoading && !viewError && blobUrl && (
                                <iframe key={blobUrl} src={blobUrl} title={selected.fileName}
                                    className="h-full w-full rounded border border-gray-300 bg-white" />
                            )}
                        </div>
                        <div className="flex items-center justify-between gap-4 border-t border-gray-200 px-6 py-3">
                            <span className="min-w-0 truncate text-xs text-gray-500">
                                {selected ? `${typeLabel(selected.documentType)} · ${selected.fileName}` : ''}
                            </span>
                            <div className="flex shrink-0 items-center gap-2">
                            {selected?.spWebUrl && (
                                <a href={selected.spWebUrl} target="_blank" rel="noopener noreferrer"
                                    className="inline-flex shrink-0 items-center gap-1 rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                                    <ExternalLink className="h-4 w-4" /> Open in SharePoint
                                </a>
                            )}
                            {selected && blobUrl && (
                                <a href={blobUrl} download={selected.fileName}
                                    className="inline-flex shrink-0 items-center gap-1 rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                                    <Download className="h-4 w-4" /> Download
                                </a>
                            )}
                            </div>
                        </div>
                    </div>
                </div>

                {deleting && (
                    <ConfirmModal
                        title="Remove document"
                        message={`Remove "${deleting.description || deleting.fileName}" from this vehicle?`}
                        variant="danger"
                        confirmLabel="Remove"
                        onConfirm={confirmDelete}
                        onCancel={() => setDeleting(null)}
                    />
                )}
            </div>
        </div>,
        document.body
    );
}
